"use client";

import React, { useState, useEffect, useCallback, useId, useRef } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type ToolUIPart, type UIMessage } from "ai";
import Link from "next/link";
import { cn } from "~/lib/utils";
import { getPostHogSupportSessionId } from "~/lib/posthog/client";
import { useSession } from "next-auth/react";
import {
  AlertCircle,
  BookOpen,
  Calendar,
  CheckCircleIcon,
  ChevronDownIcon,
  CircleIcon,
  ClockIcon,
  CopyIcon,
  HeartHandshake,
  Mail,
  MessagesSquare,
  RefreshCcwIcon,
  Search,
  ShieldCheck,
  Sparkles,
  UserRound,
  Users,
  Wallet,
  XCircleIcon,
} from "lucide-react";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "~/components/ai-elements/conversation";
import {
  MessageResponse,
  MessageActions,
  MessageAction,
} from "~/components/ai-elements/message";
import {
  Task,
  TaskContent,
  TaskItem,
  TaskTrigger,
} from "~/components/ai-elements/task";
import {
  Source,
  Sources,
  SourcesContent,
  SourcesTrigger,
} from "~/components/ai-elements/sources";
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputSubmit,
  PromptInputFooter,
} from "~/components/ai-elements/prompt-input";
import { MessageBubble } from "~/components/chat/components/message-bubble";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import {
  ToolInvocation,
  type ToolInvocationProps,
  type MatchProgressSnapshot,
} from "./therapist-matching-tool";
import type { MatchProgressEvent } from "~/app/api/asistent/types";
import { api } from "~/trpc/react";
import {
  type HelpAudience,
  HELP_AUDIENCE_SEGMENTS,
} from "~/lib/help-center-search";
import {
  getAssistantAudience,
  getGreetingMessage,
  CLIENT_PRIMARY_SUGGESTIONS,
  CLIENT_SECONDARY_SUGGESTIONS,
  LOGGED_OUT_PRIMARY_SUGGESTIONS,
  LOGGED_OUT_SECONDARY_SUGGESTIONS,
  THERAPIST_PRIMARY_SUGGESTIONS,
  THERAPIST_SECONDARY_SUGGESTIONS,
  type AssistantAudience,
} from "./assistant-ui-config";

const CHAT_INPUT_ID = "asistent-chat-input";
const LOADING_INDICATOR_DELAY_MS = 150;

const RICH_TOOL_NAMES = [
  "checkDescription",
  "prepareSearchQuery",
  "findAndSaveTherapists",
] as const;

type RichToolName = (typeof RICH_TOOL_NAMES)[number];
type AssistantToolPart = Extract<
  UIMessage["parts"][number],
  { type: string }
> & {
  type: `tool-${string}`;
  toolCallId: string;
  state: ToolUIPart["state"];
  input?: ToolUIPart["input"];
  output?: ToolUIPart["output"];
  errorText?: ToolUIPart["errorText"];
};
type AssistantTaskSourceLink = {
  href: string;
  title: string;
  subtitle?: string;
};
type AssistantTaskPresentation = {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  details?: string[];
  tone?: "default" | "error";
  sources?: AssistantTaskSourceLink[];
};

function isAssistantToolPart(
  part: UIMessage["parts"][number],
): part is AssistantToolPart {
  return typeof part.type === "string" && part.type.startsWith("tool-");
}

function getToolName(type: AssistantToolPart["type"]) {
  return type.replace("tool-", "");
}

function isVisibleAssistantPart(part: UIMessage["parts"][number]): boolean {
  if (part.type === "text") {
    return part.text.trim().length > 0;
  }

  return isAssistantToolPart(part);
}

function hasRenderableAssistantContent(messages: UIMessage[]): boolean {
  const lastMessage = messages[messages.length - 1];
  if (!lastMessage || lastMessage.role !== "assistant") {
    return false;
  }

  return lastMessage.parts.some(isVisibleAssistantPart);
}

export interface ChatAreaProps {
  onNewConversation: () => void;
  initialMessages?: UIMessage[];
  initialConversationId: string | null;
  /** Explicit viewer state. Omit to use the live session; pass null for a public surface. */
  viewer?: {
    role?: string | null;
    email?: string | null;
  } | null;
  onConversationCreated?: (conversationId: string) => void;
  onConversationStateChange?: (state: {
    conversationId: string | null;
    messages: UIMessage[];
  }) => void;
  initialInput?: string;
  onInputChange?: (input: string) => void;
  onUserMessageSubmitted?: () => void;
  pageContext?: {
    pathname: string;
    title?: string | null;
  } | null;
  /** Optional slot rendered in the header (e.g. mobile sidebar trigger) */
  headerLeft?: React.ReactNode;
  /** Optional slot rendered in the header right (e.g. new conversation button). If omitted, a default is shown. */
  headerRight?: React.ReactNode;
  /** Optional className applied to the header container. */
  headerClassName?: string;
  /** When true, hides the built-in header (for use inside app layouts with their own header). */
  hideHeader?: boolean;
  /** When true, conversation content uses the full desktop pane width instead of the centered narrow layout. */
  fullWidthDesktop?: boolean;
  /** Fixed is for the full-page public assistant; inline is for embedded overlays. */
  disclosurePlacement?: "fixed" | "inline";
  /** Autofocus is useful on the full page, but too aggressive inside mobile overlays. */
  inputAutoFocus?: boolean;
  /** The full page can afford a centered intro; embedded widgets need denser content. */
  emptyStateLayout?: "centered" | "compact";
}

export function ChatArea({
  onNewConversation,
  initialMessages,
  initialConversationId,
  viewer,
  onConversationCreated,
  onConversationStateChange,
  initialInput = "",
  onInputChange,
  onUserMessageSubmitted,
  pageContext,
  headerLeft,
  headerRight,
  headerClassName,
  hideHeader = false,
  fullWidthDesktop = false,
  disclosurePlacement = "fixed",
  inputAutoFocus = true,
  emptyStateLayout = "centered",
}: ChatAreaProps) {
  const [input, setInput] = useState(initialInput);
  const [conversationId, setConversationId] = useState<string | null>(
    initialConversationId,
  );
  const [matchProgress, setMatchProgress] = useState<
    Record<string, MatchProgressSnapshot>
  >({});
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const chatId = useId();
  const hasReceivedConversationIdRef = useRef(initialConversationId !== null);
  const pendingConversationCreatedRef = useRef<string | null>(null);

  const { data: session } = useSession();
  const effectiveViewer = viewer === undefined ? session?.user : viewer;
  const utils = api.useUtils();
  const assistantAudience = getAssistantAudience(
    effectiveViewer?.role ?? undefined,
  );

  const handleMatchProgressEvent = useCallback((event: MatchProgressEvent) => {
    setMatchProgress((previous) => {
      const existing =
        previous[event.requestId] ??
        ({
          summary: "",
          updatedAt: Date.now(),
        } as MatchProgressSnapshot);

      const snapshot: MatchProgressSnapshot = {
        ...existing,
        updatedAt: Date.now(),
      };

      switch (event.kind) {
        case "status":
          snapshot.stage = event.stage;
          if (event.message) snapshot.message = event.message;
          if (typeof event.therapistCount === "number")
            snapshot.therapistCount = event.therapistCount;
          break;
        case "sales-letter-chunk":
          snapshot.summary = `${existing.summary}${event.chunk}`;
          break;
        case "sales-letter-complete":
          snapshot.isSummaryComplete = true;
          snapshot.stage = snapshot.stage ?? "complete";
          break;
        case "results-ready":
          snapshot.resultsId = event.resultsId;
          snapshot.resultsUrl = event.resultsUrl;
          snapshot.stage = "complete";
          break;
      }

      return { ...previous, [event.requestId]: snapshot };
    });

    if (event.kind === "results-ready") {
      setActiveRequestId(null);
    } else {
      setActiveRequestId(event.requestId);
    }
  }, []);

  const { messages, sendMessage, status, error, regenerate, stop } = useChat({
    id: chatId,
    transport: new DefaultChatTransport({ api: "/api/asistent" }),
    ...(initialMessages?.length ? { messages: initialMessages } : {}),
    onData: ({ type, data }) => {
      if (type === "data-match-progress") {
        handleMatchProgressEvent(data as MatchProgressEvent);
      }
      if (type === "data-conversation-id") {
        const { conversationId: id } = data as { conversationId: string };
        setConversationId(id);
        if (!hasReceivedConversationIdRef.current) {
          hasReceivedConversationIdRef.current = true;
          pendingConversationCreatedRef.current = id;
        }
      }
    },
  });

  useEffect(() => {
    onConversationStateChange?.({ conversationId, messages });
  }, [conversationId, messages, onConversationStateChange]);

  useEffect(() => {
    onInputChange?.(input);
  }, [input, onInputChange]);

  // Refresh sidebar conversation list after each assistant response completes
  const prevStatusRef = useRef(status);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (prev !== "ready" && status === "ready" && conversationId) {
      void utils.assistant.getMyConversations.invalidate();
    }
  }, [status, conversationId, utils]);

  useEffect(() => {
    if (status !== "ready") return;

    const pendingConversationId = pendingConversationCreatedRef.current;
    if (!pendingConversationId) return;

    pendingConversationCreatedRef.current = null;
    utils.assistant.getMyConversation.setData(
      { id: pendingConversationId },
      {
        id: pendingConversationId,
        messages,
        messageCount: messages.filter(
          (message) => message.role === "user" || message.role === "assistant",
        ).length,
        therapyType: null,
        matchResultsId: null,
        escalated: false,
      },
    );
    void utils.assistant.getMyConversation.invalidate({
      id: pendingConversationId,
    });
    onConversationCreated?.(pendingConversationId);
  }, [messages, onConversationCreated, status, utils]);

  const getChatBody = () => ({
    userEmail: effectiveViewer?.email ?? null,
    conversationId,
    pageContext,
    posthogSessionId: getPostHogSupportSessionId(),
  });

  const handleSubmit = (
    message: { text: string; files: unknown[] },
    _e: React.FormEvent<HTMLFormElement>,
  ) => {
    if (!message.text.trim() || status === "streaming") return;
    onUserMessageSubmitted?.();
    sendMessage({ text: message.text }, { body: getChatBody() });
    setInput("");
  };

  const handleSuggestionClick = (text: string) => {
    onUserMessageSubmitted?.();
    sendMessage({ text }, { body: getChatBody() });
  };

  const isLoading = status === "streaming" || status === "submitted";
  const shouldShowLoadingIndicator =
    isLoading && !hasRenderableAssistantContent(messages);
  const hasStartedConversation = messages.length > 0;

  useEffect(() => {
    if (!shouldShowLoadingIndicator) {
      setShowLoadingIndicator(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowLoadingIndicator(true);
    }, LOADING_INDICATOR_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [shouldShowLoadingIndicator]);

  useEffect(() => {
    if (inputAutoFocus && status === "ready" && hasStartedConversation) {
      document.getElementById(CHAT_INPUT_ID)?.focus();
    }
  }, [inputAutoFocus, status, hasStartedConversation]);

  return (
    <>
      {!hideHeader && (
        <header
          className={cn(
            "flex h-12 flex-none items-center justify-between px-3 md:px-6",
            headerClassName,
          )}
        >
          <div className="flex items-center gap-1">{headerLeft}</div>
          {headerRight ??
            (hasStartedConversation ? (
              <button
                onClick={onNewConversation}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Novi razgovor
              </button>
            ) : null)}
        </header>
      )}

      <Conversation
        className={cn(
          "flex min-h-0 w-full flex-col",
          "flex-1",
          fullWidthDesktop ? "max-w-none" : "mx-auto max-w-4xl",
        )}
      >
        <ConversationContent
          className={cn(
            hasStartedConversation
              ? "px-4 py-4"
              : "h-full flex-1 px-4 pb-0 pt-0",
            fullWidthDesktop ? "md:px-8 lg:px-10" : "md:px-6",
          )}
        >
          {hasStartedConversation ? (
            <>
              <MessageList
                messages={messages}
                isLoading={isLoading}
                regenerate={regenerate}
                matchProgress={matchProgress}
                activeRequestId={activeRequestId}
              />
              {showLoadingIndicator && <LoadingIndicator />}
              {error && <ErrorAlert />}
            </>
          ) : (
            <EmptyState
              audience={assistantAudience}
              input={input}
              setInput={setInput}
              isLoading={isLoading}
              onSubmit={handleSubmit}
              onStop={stop}
              isLoggedIn={!!effectiveViewer}
              onSuggestionClick={handleSuggestionClick}
              inputAutoFocus={inputAutoFocus}
              layout={emptyStateLayout}
            />
          )}
        </ConversationContent>
        {hasStartedConversation && (
          <ConversationScrollButton className="bottom-2" />
        )}
      </Conversation>

      {hasStartedConversation && (
        <div className="flex-none">
          <div className={cn("pb-safe px-3 md:px-6", "pb-3 pt-2 md:pb-4")}>
            <div
              className={cn(
                "w-full",
                fullWidthDesktop
                  ? "max-w-none"
                  : hasStartedConversation
                    ? "mx-auto max-w-4xl"
                    : "mx-auto max-w-2xl",
              )}
            >
              <ChatInput
                input={input}
                setInput={setInput}
                isLoading={isLoading}
                onSubmit={handleSubmit}
                onStop={stop}
                autoFocus={inputAutoFocus}
              />
            </div>
          </div>
        </div>
      )}

      {!hasStartedConversation && !effectiveViewer && (
        <AssistantPublicDisclosure placement={disclosurePlacement} />
      )}
    </>
  );
}

function AssistantPublicDisclosure({
  placement,
}: {
  placement: "fixed" | "inline";
}) {
  return (
    <footer
      className={cn(
        "pointer-events-none text-center text-[11px] leading-snug text-muted-foreground sm:text-xs",
        placement === "fixed"
          ? "fixed inset-x-0 bottom-4 z-10 px-20 sm:px-4"
          : "shrink-0 px-4 pb-3 pt-2",
      )}
    >
      <p className="pointer-events-auto mx-auto max-w-2xl">
        Rešimo Probleme koristi veštačku inteligenciju. Korišćenjem ovog četa,
        prihvataš naše{" "}
        <Link
          href="/legal/uslovi-koriscenja"
          className="underline underline-offset-2"
        >
          uslove korišćenja
        </Link>
        {" i "}
        <Link
          href="/legal/politika-privatnosti"
          className="underline underline-offset-2"
        >
          politiku privatnosti
        </Link>
        . Sadržaj ovog četa može se iskoristiti radi unapređenja našeg sistema.{" "}
        <Link
          href="/podrska/klijenti/ai-asistent"
          className="underline underline-offset-2"
        >
          Saznaj više
        </Link>
        .
      </p>
    </footer>
  );
}

function EmptyState({
  audience,
  input,
  setInput,
  isLoading,
  onSubmit,
  onStop,
  isLoggedIn,
  onSuggestionClick,
  inputAutoFocus,
  layout,
}: {
  audience: AssistantAudience;
  input: string;
  setInput: (value: string) => void;
  isLoading: boolean;
  onSubmit: (
    message: { text: string; files: unknown[] },
    e: React.FormEvent<HTMLFormElement>,
  ) => void;
  onStop?: () => void;
  isLoggedIn: boolean;
  onSuggestionClick: (text: string) => void;
  inputAutoFocus: boolean;
  layout: "centered" | "compact";
}) {
  const title = "Kako možemo da ti pomognemo danas?";
  const isCompact = layout === "compact";

  return (
    <div
      className={cn(
        "flex min-h-full flex-col items-center px-3 md:px-6",
        isCompact ? "justify-start pb-4 pt-6" : "justify-center pb-16 pt-8",
      )}
    >
      <div className="w-full max-w-2xl">
        <div className={cn("text-center", isCompact ? "sr-only" : "mb-7")}>
          <h1 className="h1-content mb-3">{title}</h1>
        </div>

        <ChatInput
          input={input}
          setInput={setInput}
          isLoading={isLoading}
          onSubmit={onSubmit}
          onStop={onStop}
          variant="start"
          autoFocus={inputAutoFocus}
        />

        <SuggestionChips
          audience={audience}
          isLoggedIn={isLoggedIn}
          onSuggestionClick={onSuggestionClick}
        />
      </div>
    </div>
  );
}

function SuggestionChips({
  audience,
  isLoggedIn,
  onSuggestionClick,
}: {
  audience: AssistantAudience;
  isLoggedIn: boolean;
  onSuggestionClick: (text: string) => void;
}) {
  const primaryRow =
    audience === "therapist"
      ? THERAPIST_PRIMARY_SUGGESTIONS
      : isLoggedIn
        ? CLIENT_PRIMARY_SUGGESTIONS
        : LOGGED_OUT_PRIMARY_SUGGESTIONS;
  const secondaryRow =
    audience === "therapist"
      ? THERAPIST_SECONDARY_SUGGESTIONS
      : isLoggedIn
        ? CLIENT_SECONDARY_SUGGESTIONS
        : LOGGED_OUT_SECONDARY_SUGGESTIONS;

  return (
    <div className="mt-4 space-y-2">
      <div className="flex flex-wrap justify-center gap-2">
        {primaryRow.map((s) => (
          <button
            key={s}
            onClick={() => onSuggestionClick(s)}
            className="rounded-full border border-border bg-muted/60 px-3.5 py-1.5 text-sm font-medium text-foreground shadow-xs transition-colors hover:border-primary/30 hover:bg-primary/10"
          >
            {s}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        {secondaryRow.map((s) => (
          <button
            key={s}
            onClick={() => onSuggestionClick(s)}
            className="rounded-full border border-border/70 bg-background px-3.5 py-1.5 text-sm text-muted-foreground transition-colors hover:border-foreground/20 hover:bg-muted hover:text-foreground"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatInput({
  input,
  setInput,
  isLoading,
  onSubmit,
  onStop,
  variant = "conversation",
  autoFocus = true,
}: {
  input: string;
  setInput: (value: string) => void;
  isLoading: boolean;
  onSubmit: (
    message: { text: string; files: unknown[] },
    e: React.FormEvent<HTMLFormElement>,
  ) => void;
  onStop?: () => void;
  variant?: "conversation" | "start";
  autoFocus?: boolean;
}) {
  return (
    <PromptInput onSubmit={onSubmit} className="bg-background">
      <PromptInputTextarea
        id={CHAT_INPUT_ID}
        value={input}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          setInput(e.target.value)
        }
        placeholder="Pitaj šta te zanima ili opiši svoj problem..."
        disabled={isLoading}
        className={cn(
          "text-base",
          variant === "start" && "min-h-24 px-4 pt-4 md:min-h-12",
        )}
        autoFocus={autoFocus}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (input.trim() && !isLoading) {
              e.currentTarget.form?.requestSubmit();
            }
          }
        }}
      />
      <PromptInputFooter className="justify-end">
        <PromptInputSubmit
          aria-label={isLoading ? "Zaustavi odgovor" : "Pošalji poruku"}
          status={isLoading ? "streaming" : "ready"}
          disabled={!input.trim() && !isLoading}
          className={cn(
            "transition-colors",
            input.trim() &&
              !isLoading &&
              "bg-brand-action text-brand-foreground hover:bg-brand-action-hover",
          )}
          onClick={
            isLoading && onStop
              ? (e) => {
                  e.preventDefault();
                  onStop();
                }
              : undefined
          }
          type={isLoading && onStop ? "button" : "submit"}
        />
      </PromptInputFooter>
    </PromptInput>
  );
}

function LoadingIndicator() {
  return (
    <div className="mr-auto max-w-[92%] sm:max-w-[85%]">
      <MessageBubble senderName="Podrška" avatarVariant="assistant">
        <div
          className="flex items-center gap-1 py-0.5"
          aria-label="Podrška piše..."
        >
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:0ms]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:150ms]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:300ms]" />
        </div>
      </MessageBubble>
    </div>
  );
}

function ErrorAlert() {
  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Greška</AlertTitle>
      <AlertDescription>
        Došlo je do greške. Molim pokušaj ponovo.
      </AlertDescription>
    </Alert>
  );
}

function MessageList({
  messages,
  isLoading,
  regenerate,
  matchProgress,
  activeRequestId,
}: {
  messages: UIMessage[];
  isLoading: boolean;
  regenerate: (() => void) | undefined;
  matchProgress: Record<string, MatchProgressSnapshot>;
  activeRequestId: string | null;
}) {
  return (
    <>
      {messages.map((message, messageIndex) => (
        <div key={message.id} className="flex flex-col gap-4">
          {message.parts.map((part, partIndex) => {
            if (part.type === "text") {
              if (!isVisibleAssistantPart(part)) {
                return null;
              }

              return (
                <TextMessagePart
                  key={`${message.id}-${partIndex}`}
                  message={message}
                  part={part}
                  isLastMessage={messageIndex === messages.length - 1}
                  isLastPart={partIndex === message.parts.length - 1}
                  isLoading={isLoading}
                  regenerate={regenerate}
                />
              );
            }

            if (isAssistantToolPart(part)) {
              return (
                <AssistantToolPartView
                  key={`${message.id}-${partIndex}`}
                  part={part}
                  matchProgress={matchProgress}
                  activeRequestId={activeRequestId}
                />
              );
            }

            return null;
          })}
        </div>
      ))}
    </>
  );
}

function TextMessagePart({
  message,
  part,
  isLastMessage,
  isLastPart,
  isLoading,
  regenerate,
}: {
  message: UIMessage;
  part: { type: "text"; text: string };
  isLastMessage: boolean;
  isLastPart: boolean;
  isLoading: boolean;
  regenerate: (() => void) | undefined;
}) {
  const showActions =
    message.role === "assistant" && isLastMessage && isLastPart;

  return (
    <>
      {message.role === "user" ? (
        <UserTextBubble text={part.text} />
      ) : (
        <AssistantTextBubble text={part.text} />
      )}
      {showActions && (
        <MessageActions className="ml-10">
          <MessageAction
            onClick={() => regenerate?.()}
            label="Ponovi"
            tooltip="Ponovi odgovor"
            disabled={!regenerate || isLoading}
          >
            <RefreshCcwIcon className="size-3" />
          </MessageAction>
          <MessageAction
            onClick={() => navigator.clipboard.writeText(part.text)}
            label="Kopiraj"
            tooltip="Kopiraj tekst"
          >
            <CopyIcon className="size-3" />
          </MessageAction>
        </MessageActions>
      )}
    </>
  );
}

type AssistantActionLink = {
  href: string;
  label: string;
};

function extractAssistantActionLinks(text: string): AssistantActionLink[] {
  const markdownLinks = text.matchAll(/\[([^\]]+)\]\(([^)]+)\)/g);
  const actions: AssistantActionLink[] = [];
  const seen = new Set<string>();

  for (const match of markdownLinks) {
    const label = match[1]?.trim();
    const href = match[2]?.trim();
    if (!label || !href) continue;

    const isAction =
      href.includes("/app/login") ||
      (href.includes("/terapeuti/") && href.includes("/book")) ||
      href.includes("/app/settings/") ||
      href.includes("/app/inbox");

    if (!isAction || seen.has(href)) continue;
    seen.add(href);
    actions.push({ href, label });
  }

  return actions.slice(0, 4);
}

function AssistantTextBubble({ text }: { text: string }) {
  const actionLinks = extractAssistantActionLinks(text);

  return (
    <div className="mr-auto max-w-[92%] sm:max-w-[85%]">
      <MessageBubble senderName="Podrška" avatarVariant="assistant">
        <MessageResponse>{text}</MessageResponse>
        {actionLinks.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {actionLinks.map((action) => (
              <a
                key={`${action.href}-${action.label}`}
                href={action.href}
                className="inline-flex min-h-9 items-center rounded-md border border-brand/25 bg-brand-action px-3 py-1.5 text-sm font-medium text-brand-foreground transition-colors hover:bg-brand-action-hover"
              >
                {action.label}
              </a>
            ))}
          </div>
        )}
      </MessageBubble>
    </div>
  );
}

function UserTextBubble({ text }: { text: string }) {
  return (
    <div className="ml-auto max-w-[92%] sm:max-w-[85%]">
      <MessageBubble isMine senderName="Ti" avatarVariant="user">
        <span className="whitespace-pre-wrap">{text}</span>
      </MessageBubble>
    </div>
  );
}

function AssistantToolPartView({
  part,
  matchProgress,
  activeRequestId,
}: {
  part: AssistantToolPart;
  matchProgress: Record<string, MatchProgressSnapshot>;
  activeRequestId: string | null;
}) {
  const toolName = getToolName(part.type);
  let progress: MatchProgressSnapshot | undefined;
  if (toolName === "findAndSaveTherapists") {
    const outputWithRequest = part.output as { requestId?: string } | undefined;
    const requestId = outputWithRequest?.requestId ?? activeRequestId ?? null;
    if (requestId) {
      progress = matchProgress[requestId];
    }
  }

  const isRichTool = RICH_TOOL_NAMES.includes(toolName as RichToolName);
  const richContent = isRichTool ? (
    <ToolInvocation
      toolCallId={part.toolCallId}
      toolName={toolName as RichToolName}
      state={part.state}
      result={
        part.state === "output-available"
          ? (part.output as ToolInvocationProps["result"])
          : undefined
      }
      input={part.input as ToolInvocationProps["input"]}
      error={part.state === "output-error" ? part.errorText : undefined}
      progress={progress}
    />
  ) : null;
  const taskPresentation = richContent
    ? null
    : buildAssistantTaskPresentation({ toolName, part });

  if (richContent) {
    return richContent;
  }

  return (
    <AssistantTaskCard
      presentation={taskPresentation!}
      state={part.state}
      defaultOpen={part.state !== "output-available"}
    />
  );
}

function AssistantTaskCard({
  presentation,
  state,
  defaultOpen,
}: {
  presentation: AssistantTaskPresentation;
  state: ToolUIPart["state"];
  defaultOpen: boolean;
}) {
  const status = getAssistantTaskStatusMeta(state, presentation.tone);
  const Icon = presentation.icon;
  const tone =
    presentation.tone === "error"
      ? {
          iconContainer: "bg-destructive/10 text-destructive",
          detail: "text-destructive",
        }
      : {
          iconContainer: "bg-secondary text-brand-action",
          detail: "text-foreground",
        };

  return (
    <div className="mr-auto max-w-[92%] py-1 sm:max-w-[85%]">
      <Task defaultOpen={defaultOpen} className="w-full">
        <TaskTrigger title={presentation.title}>
          <div className="flex w-full items-start justify-between gap-2 py-1 sm:items-center sm:gap-4">
            <div className="flex min-w-0 items-start gap-2 sm:items-center sm:gap-3">
              <div
                className={cn(
                  "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full sm:mt-0 sm:h-9 sm:w-9",
                  tone.iconContainer,
                )}
              >
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="line-clamp-2 text-sm font-medium leading-5 text-foreground sm:truncate sm:leading-normal">
                  {presentation.title}
                </p>
                <p className="mt-0.5 line-clamp-1 hidden text-sm text-muted-foreground sm:block">
                  {presentation.description}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1 sm:gap-2">
              <Badge
                className="gap-1 rounded-full px-2 py-0.5 text-[11px] sm:gap-1.5 sm:text-xs"
                variant="secondary"
              >
                <status.icon className={cn("size-4", status.iconClassName)} />
                {status.label}
              </Badge>
              <ChevronDownIcon className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
            </div>
          </div>
        </TaskTrigger>
        <TaskContent className="pb-0">
          {(presentation.details?.length ?? 0) > 0 ? (
            presentation.details?.map((detail) => (
              <TaskItem key={detail} className={cn("leading-6", tone.detail)}>
                {detail}
              </TaskItem>
            ))
          ) : (
            <TaskItem className={cn("leading-6", tone.detail)}>
              {presentation.description}
            </TaskItem>
          )}
          {presentation.sources && presentation.sources.length > 0 ? (
            <Sources className="mb-0 mt-3">
              <SourcesTrigger count={presentation.sources.length}>
                <div className="flex items-center gap-2 text-xs text-primary">
                  <BookOpen className="h-4 w-4" />
                  <span className="font-medium">
                    Korišćeni izvori ({presentation.sources.length})
                  </span>
                  <ChevronDownIcon className="h-4 w-4" />
                </div>
              </SourcesTrigger>
              <SourcesContent>
                {presentation.sources.map((source) => (
                  <Source
                    href={source.href}
                    key={`${source.href}-${source.title}-${source.subtitle ?? ""}`}
                    title={source.title}
                  >
                    <div className="flex items-start gap-2 text-left">
                      <BookOpen className="mt-0.5 h-4 w-4 shrink-0" />
                      <div className="space-y-0.5">
                        <span className="block font-medium">
                          {source.title}
                        </span>
                        {source.subtitle ? (
                          <span className="block text-xs text-muted-foreground">
                            {source.subtitle}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </Source>
                ))}
              </SourcesContent>
            </Sources>
          ) : null}
        </TaskContent>
      </Task>
    </div>
  );
}

function getToolLabel(toolName: string) {
  const labels: Record<string, string> = {
    checkDescription: "Provera opisa",
    checkIfCouplesTherapyNeeded: "Provera tipa terapije",
    prepareSearchQuery: "Priprema pretrage",
    findAndSaveTherapists: "Pretraga terapeuta",
    searchKnowledgeBase: "Pretraga centra za pomoć",
    searchPublicTherapists: "Pretraga javnih profila terapeuta",
    readKnowledgeBaseSection: "Čitanje članka",
    escalateToSupport: "Prosleđivanje podršci",
    getVerifiedTherapistPricingSummary: "Pregled cena i trajanja",
    getMyTherapists: "Provera terapeuta",
    getMyAiAccessStatus: "Provera AI pristupa",
    getMyUpcomingSchedule: "Pregled rasporeda",
    getMyPaymentsSummary: "Pregled uplata",
    getMyQuizResults: "Provera testova",
    getMyMessageSummaries: "Pregled poruka",
    getMySessionsSchedule: "Raspored sesija",
    getMyClients: "Pregled klijenata",
    getClientSnapshot: "Pregled klijenta",
    getClientConversationSummary: "Sažetak razgovora",
    getClientsNeedingAttention: "Klijenti koji traže pažnju",
    getTherapistEarningsSummary: "Pregled zarade",
    getSessionPoliciesForContext: "Pravila za sesiju",
  };

  return labels[toolName] ?? toolName;
}

function getAssistantTaskStatusMeta(
  state: ToolUIPart["state"],
  tone?: AssistantTaskPresentation["tone"],
) {
  if (state === "output-available" && tone === "error") {
    return {
      label: "Greška",
      icon: XCircleIcon,
      iconClassName: "text-destructive",
    };
  }

  switch (state) {
    case "input-streaming":
      return {
        label: "Priprema",
        icon: CircleIcon,
        iconClassName: "",
      };
    case "input-available":
      return {
        label: "U toku",
        icon: ClockIcon,
        iconClassName: "animate-pulse",
      };
    case "output-error":
      return {
        label: "Greška",
        icon: XCircleIcon,
        iconClassName: "text-destructive",
      };
    case "output-available":
    default:
      return {
        label: "Završeno",
        icon: CheckCircleIcon,
        iconClassName: "text-brand",
      };
  }
}

function buildAssistantTaskPresentation({
  toolName,
  part,
}: {
  toolName: string;
  part: AssistantToolPart;
}): AssistantTaskPresentation {
  const output = isRecord(part.output) ? part.output : null;

  if (part.state === "output-error") {
    return {
      icon: AlertCircle,
      title: "Došlo je do greške",
      description: part.errorText ?? "Pokušaj ponovo.",
      tone: "error",
    };
  }

  if (part.state === "input-streaming") {
    return {
      icon: Sparkles,
      title: getPendingToolTitle(toolName),
      description: "Pripremam ovaj korak.",
    };
  }

  if (part.state === "input-available") {
    return {
      icon: Sparkles,
      title: getPendingToolTitle(toolName),
      description: "Izvršavam ovaj korak.",
    };
  }

  switch (toolName) {
    case "searchKnowledgeBase":
      return renderKnowledgeBaseSearchSummary(part.input, output);
    case "searchPublicTherapists":
      return renderPublicTherapistsSummary(part.input, output);
    case "readKnowledgeBaseSection":
      return renderKnowledgeBaseReadSummary(output);
    case "checkIfCouplesTherapyNeeded":
      return renderCouplesCheckSummary(output);
    case "escalateToSupport":
      return renderEscalationSummary(output);
    case "getVerifiedTherapistPricingSummary":
      return renderVerifiedTherapistPricingSummary(output);
    case "getMyTherapists":
      return renderMyTherapistsSummary(output);
    case "getMyAiAccessStatus":
      return renderMyAiAccessSummary(output);
    case "getMyUpcomingSchedule":
      return renderUpcomingScheduleSummary(output);
    case "getMyPaymentsSummary":
      return renderPaymentsSummary(output);
    case "getMyBookingEntitlements":
      return renderBookingEntitlementsSummary(output);
    case "getMyQuizResults":
      return renderMyQuizResultsSummary(output);
    case "getMyMessageSummaries":
      return renderMyMessageSummariesSummary(output);
    case "getMySessionsSchedule":
      return renderSessionsScheduleSummary(output);
    case "getMyClients":
      return renderMyClientsSummary(output);
    case "getClientSnapshot":
      return renderClientSnapshotSummary(output);
    case "getClientsNeedingAttention":
      return renderClientsNeedingAttentionSummary(output);
    case "getClientConversationSummary":
      return renderClientConversationSummary(output);
    case "getTherapistEarningsSummary":
      return renderTherapistEarningsSummary(output);
    case "getSessionPoliciesForContext":
      return renderSessionPoliciesSummary(output);
    default:
      return renderFallbackToolSummary(toolName, output);
  }
}

function getPendingToolTitle(toolName: string) {
  const titles: Record<string, string> = {
    searchKnowledgeBase: "Pretražujem podršku",
    searchPublicTherapists: "Pretražujem javne profile terapeuta",
    readKnowledgeBaseSection: "Otvaram članak centra za pomoć",
    escalateToSupport: "Prosleđujem podršci",
    getVerifiedTherapistPricingSummary: "Proveravam aktuelne cene i trajanje",
    getMyTherapists: "Proveravam tvoje terapeute",
    getMyAiAccessStatus: "Proveravam tvoj AI paket",
    getMyUpcomingSchedule: "Proveravam tvoj raspored",
    getMyPaymentsSummary: "Proveravam uplate i kredite",
    getMyQuizResults: "Proveravam tvoje testove",
    getMyMessageSummaries: "Pregledam poruke",
    getMySessionsSchedule: "Proveravam raspored sesija",
    getMyClients: "Proveravam klijente",
    getClientSnapshot: "Prikupljam pregled klijenta",
    getClientsNeedingAttention: "Tražim prioritete među klijentima",
    getClientConversationSummary: "Pravim sažetak razgovora",
    getTherapistEarningsSummary: "Proveravam zaradu i isplate",
    getSessionPoliciesForContext: "Proveravam pravila za sesiju",
    checkIfCouplesTherapyNeeded:
      "Proveravam da li više odgovara partnerska terapija",
  };

  return titles[toolName] ?? "Podrška radi u pozadini";
}

function renderKnowledgeBaseSearchSummary(
  input: ToolUIPart["input"],
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const query =
    isRecord(input) && typeof input.query === "string" ? input.query : null;
  const results = getArrayOfRecords(output?.results);
  const details = [
    query ? `Pretraženo za: "${query}".` : "Pretražena je podrška.",
    `${results.length} ${pluralize(results.length, "relevantan članak", "relevantna članka", "relevantnih članaka")} pronađeno.`,
    ...results
      .slice(0, 4)
      .map((result) => {
        const title = getString(result.title);
        const heading = getString(result.heading);
        if (!title) return null;
        return heading ? `${title} · ${heading}` : title;
      })
      .filter((detail): detail is string => Boolean(detail)),
  ];

  return {
    icon: Search,
    title: "Pretraga centra za pomoć",
    description:
      results.length > 0
        ? "Pronašao/la sam relevantne članke za ovaj upit."
        : (getString(output?.message) ?? "Pretraga je završena bez rezultata."),
    details,
    sources: buildKnowledgeBaseSources(results),
  };
}

function renderPublicTherapistsSummary(
  input: ToolUIPart["input"],
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const inputRecord = isRecord(input) ? input : null;
  const query = getString(inputRecord?.query);
  const therapistName = getString(inputRecord?.therapistName);
  const therapists = getArrayOfRecords(output?.therapists);
  const details = [
    therapistName ? `Tražen terapeut: ${therapistName}.` : null,
    query ? `Tema pretrage: "${query}".` : null,
    `${therapists.length} ${pluralize(therapists.length, "profil", "profila", "profila")} pronađeno.`,
    ...therapists.slice(0, 4).map((therapist) => {
      const name = getString(therapist.name);
      const title = getString(therapist.title);
      const specialties = getArrayOfStrings(therapist.specialties).slice(0, 2);

      if (!name) return null;

      return [name, title, specialties.join(", ")]
        .filter((item): item is string => Boolean(item))
        .join(" · ");
    }),
  ].filter((detail): detail is string => Boolean(detail));

  return {
    icon: Users,
    title: "Javni profili terapeuta",
    description:
      therapists.length > 0
        ? "Pronašao/la sam relevantne javne profile za ovu proveru."
        : (getString(output?.message) ?? "Nema odgovarajućih javnih profila."),
    details,
    sources: therapists
      .reduce<AssistantTaskSourceLink[]>((acc, therapist) => {
        const href = getString(therapist.profileUrl);
        const title = getString(therapist.name);
        const subtitle = getString(therapist.title);

        if (!href || !title) return acc;

        acc.push({ href, title, subtitle: subtitle ?? undefined });
        return acc;
      }, [])
      .slice(0, 4),
  };
}

function renderKnowledgeBaseReadSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const title = getString(output?.title);
  const heading = getString(output?.heading);
  const source = getString(output?.file);
  const audience = getHelpAudience(output?.audience);
  const href = source ? buildHelpArticleHref(source, audience) : null;

  return {
    icon: BookOpen,
    title: "Čitanje članka",
    description: "Otvoren je članak iz centra za pomoć radi tačnog odgovora.",
    details: [title, heading].filter((item): item is string => Boolean(item)),
    sources:
      href && title
        ? [
            {
              href,
              title,
              subtitle: heading ?? undefined,
            },
          ]
        : [],
  };
}

function renderCouplesCheckSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const shouldAsk = getBoolean(output?.shouldAsk);
  const reason = getString(output?.reason);

  return {
    icon: HeartHandshake,
    title: "Provera tipa terapije",
    description: shouldAsk
      ? "Deluje da vredi proveriti da li je partnerska terapija bolji fit."
      : "Zadržavam fokus na individualnoj terapiji.",
    details: reason ? [reason] : [],
  };
}

function renderEscalationSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const success = getBoolean(output?.success);
  const message = getString(output?.message) ?? getString(output?.error);

  return {
    icon: Mail,
    title: "Prosleđivanje podršci",
    description: message ?? "Zabeležen je zahtev za podršku.",
    tone: success === false ? "error" : "default",
  };
}

function renderVerifiedTherapistPricingSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const therapyType = getString(output?.therapyType);
  const minPrice = getNumber(output?.minPriceEur);
  const maxPrice = getNumber(output?.maxPriceEur);
  const minDuration = getNumber(output?.minDurationMinutes);
  const maxDuration = getNumber(output?.maxDurationMinutes);
  const examples = getArrayOfRecords(output?.exampleTherapists);
  const count = getNumber(output?.verifiedTherapistCount);

  const details = [
    typeof minPrice === "number" && typeof maxPrice === "number"
      ? `Raspon cena: ${minPrice}€-${maxPrice}€`
      : null,
    typeof minDuration === "number" && typeof maxDuration === "number"
      ? `Trajanje: ${minDuration}-${maxDuration} min`
      : null,
    typeof count === "number"
      ? `Verifikovanih terapeuta u pregledu: ${count}`
      : null,
    ...examples.slice(0, 3).map((example) => {
      const name = getString(example.name);
      const price = getNumber(example.priceEur);
      const duration = getNumber(example.durationMinutes);

      if (!name || typeof price !== "number" || typeof duration !== "number") {
        return null;
      }

      return `${name} · ${price}€ · ${duration} min`;
    }),
  ].filter((detail): detail is string => Boolean(detail));

  return {
    icon: Wallet,
    title:
      therapyType === "COUPLES"
        ? "Cene partnerske terapije"
        : "Cene individualne terapije",
    description:
      getString(output?.message) ??
      "Prikupio sam aktuelne cene i trajanja za verifikovane terapeute.",
    details,
  };
}

function renderMyTherapistsSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const therapists = getArrayOfRecords(output?.therapists);
  const names = therapists
    .map((therapist) => getString(therapist.name))
    .filter((name): name is string => Boolean(name))
    .slice(0, 4);

  return {
    icon: Users,
    title: "Tvoji terapeuti",
    description:
      therapists.length > 0
        ? `Pronašao/la sam ${therapists.length} ${pluralize(therapists.length, "terapeuta", "terapeuta", "terapeuta")}.`
        : (getString(output?.message) ?? "Nema aktivnih terapeuta."),
    details: names,
  };
}

function renderMyAiAccessSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const success = getBoolean(output?.success);
  const summary = getString(output?.summary);
  const deletionEffect = getString(output?.deletionEffect);
  const activeSubscription = isRecord(output?.activeSubscription)
    ? output.activeSubscription
    : null;
  const duration = getString(activeSubscription?.duration);
  const expiresAt = getString(activeSubscription?.expiresAt);

  const details = [deletionEffect];
  if (duration && expiresAt) {
    details.unshift(`Aktivan paket: ${duration} · važi do ${expiresAt}`);
  }

  return {
    icon: Wallet,
    title: "AI pristup",
    description:
      summary ??
      (success === false
        ? (getString(output?.error) ??
          "Ne mogu trenutno da proverim AI pristup.")
        : "Proverio sam status AI pristupa."),
    details: details.filter((detail): detail is string => Boolean(detail)),
    tone: success === false ? "error" : "default",
  };
}

function renderUpcomingScheduleSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const days = getArrayOfRecords(output?.days);
  const dateFrom = getString(output?.dateFrom);
  const dateTo = getString(output?.dateTo);
  const totalSessions = getNumber(
    output?.totals && isRecord(output.totals) ? output.totals.total : null,
  );
  const flattenedSessions = days.flatMap((day) => {
    const dayLabel = getString(day.dateLabel);
    const sessions = getArrayOfRecords(day.sessions);

    return sessions.map((session) => ({
      dayLabel,
      localTime: getString(session.localTime),
      therapistName: getString(session.therapistName),
    }));
  });

  return {
    icon: Calendar,
    title: "Pregled rasporeda",
    description:
      flattenedSessions.length > 0
        ? `Za period ${dateFrom ?? "odabrani datum"} do ${dateTo ?? "odabrani datum"} imaš ${totalSessions ?? flattenedSessions.length} ${pluralize(totalSessions ?? flattenedSessions.length, "sesiju", "sesije", "sesija")}.`
        : (getString(output?.message) ?? "Nema sesija u traženom periodu."),
    details: flattenedSessions
      .slice(0, 4)
      .map((session) =>
        [session.dayLabel, session.localTime, session.therapistName]
          .filter((item): item is string => Boolean(item))
          .join(" · "),
      )
      .filter(Boolean),
  };
}

function renderPaymentsSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const totals = isRecord(output?.totals) ? output.totals : null;
  const paidAmount = getNumber(totals?.paidAmountEur) ?? 0;
  const refundedAmount = getNumber(totals?.refundedAmountEur) ?? 0;
  const legacyPackageSessions = getNumber(totals?.creditsRemaining);
  const legacyPackageSuffix =
    legacyPackageSessions === null
      ? ""
      : ` Preostale sesije u paketima: ${legacyPackageSessions}.`;

  return {
    icon: Wallet,
    title: "Pregled uplata",
    description: `Uplate: ${paidAmount}€. Refund: ${refundedAmount}€.${legacyPackageSuffix}`,
  };
}

function renderBookingEntitlementsSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const sessions = getArrayOfRecords(output?.sessionsByTherapist);
  const accountCredit = getNumber(output?.accountCreditEur) ?? 0;
  return {
    icon: Wallet,
    title: "Preostale sesije i kredit",
    description: `${sessions.length} ${pluralize(sessions.length, "terapeut", "terapeuta", "terapeuta")} sa preostalim sesijama. Kredit na nalogu: ${accountCredit}€.`,
    details: sessions.map((item) =>
      `${getString(item.therapistName) ?? "Terapeut"}: ${getNumber(item.remainingSessions) ?? 0} sesija`,
    ),
  };
}

function renderMyQuizResultsSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const quizzes = getArrayOfRecords(output?.quizzes);
  const latest = getString(quizzes[0]?.type);

  return {
    icon: BookOpen,
    title: "Pregled testova",
    description:
      quizzes.length > 0
        ? `Pronašao/la sam ${quizzes.length} ${pluralize(quizzes.length, "rezultat", "rezultata", "rezultata")}.`
        : "Nema sačuvanih rezultata testova.",
    details: latest ? [`Najnoviji test: ${latest}`] : [],
  };
}

function renderMyMessageSummariesSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const summaries = getArrayOfRecords(output?.summaries);
  const period = getString(output?.period);
  const therapistNames = summaries
    .map(
      (summary) =>
        getString(summary.relationshipLabel) ??
        getString(summary.therapistName),
    )
    .filter((name): name is string => Boolean(name))
    .slice(0, 4);

  return {
    icon: MessagesSquare,
    title: "Pregled poruka",
    description:
      summaries.length > 0
        ? `Sažeto je ${summaries.length} ${pluralize(summaries.length, "razgovor", "razgovora", "razgovora")}${period ? ` za period: ${period}` : "."}`
        : (getString(output?.message) ?? "Nema dostupnih poruka za pregled."),
    details: therapistNames,
  };
}

function renderSessionsScheduleSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const days = getArrayOfRecords(output?.days);
  const dateFrom = getString(output?.dateFrom);
  const dateTo = getString(output?.dateTo);
  const totalSessions = getNumber(
    output?.totals && isRecord(output.totals) ? output.totals.total : null,
  );
  const flattenedSessions = days.flatMap((day) => {
    const dayLabel = getString(day.dateLabel);
    const sessions = getArrayOfRecords(day.sessions);

    return sessions.map((session) => ({
      dayLabel,
      time: getString(session.time),
      clientName: getString(session.clientName),
    }));
  });
  const isSingleDay = dateFrom && dateTo && dateFrom === dateTo;

  return {
    icon: Calendar,
    title: "Raspored sesija",
    description:
      flattenedSessions.length > 0
        ? isSingleDay
          ? `Za ${getString(days[0]?.dateLabel) ?? "traženi datum"} imaš ${totalSessions ?? flattenedSessions.length} ${pluralize(totalSessions ?? flattenedSessions.length, "sesiju", "sesije", "sesija")}.`
          : `Za period ${dateFrom ?? "odabrani datum"} do ${dateTo ?? "odabrani datum"} imaš ${totalSessions ?? flattenedSessions.length} ${pluralize(totalSessions ?? flattenedSessions.length, "sesiju", "sesije", "sesija")}.`
        : (getString(output?.message) ?? "Nema sesija u traženom periodu."),
    details: flattenedSessions
      .slice(0, 4)
      .map((session) =>
        [session.dayLabel, session.time, session.clientName]
          .filter((item): item is string => Boolean(item))
          .join(" · "),
      )
      .filter(Boolean),
  };
}

function renderMyClientsSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const clients = getArrayOfRecords(output?.clients);
  const namesNeedingReply = clients
    .filter((client) => getBoolean(client.needsReply))
    .map((client) => getString(client.clientName))
    .filter((name): name is string => Boolean(name))
    .slice(0, 4);

  const details = [];
  if (namesNeedingReply.length > 0) {
    details.push(`Traže odgovor: ${namesNeedingReply.join(", ")}`);
  }

  return {
    icon: UserRound,
    title: "Pregled klijenata",
    description:
      clients.length > 0
        ? `Prikazano je ${clients.length} ${pluralize(clients.length, "klijent", "klijenta", "klijenata")}.`
        : (getString(output?.message) ?? "Nema klijenata za prikaz."),
    details,
  };
}

function renderClientSnapshotSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const clientName = getString(output?.clientName);
  const unreadMessageCount = getNumber(output?.unreadMessageCount) ?? 0;
  const reasons = getArrayOfStrings(output?.attentionReasons).slice(0, 3);
  const nextSession = isRecord(output?.nextSession) ? output.nextSession : null;
  const nextSessionTime = getString(nextSession?.localTime);
  const nextSessionDate = getString(nextSession?.localDate);

  const details = [];
  if (nextSessionDate && nextSessionTime) {
    details.push(`Sledeća sesija: ${nextSessionDate} u ${nextSessionTime}`);
  }
  if (unreadMessageCount > 0) {
    details.push(`Nepročitane poruke: ${unreadMessageCount}`);
  }
  details.push(...reasons);

  return {
    icon: UserRound,
    title: "Pregled klijenta",
    description: clientName
      ? `Prikazan je sažet pregled za ${clientName}.`
      : "Prikazan je sažet pregled klijenta.",
    details,
  };
}

function renderClientsNeedingAttentionSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const clients = getArrayOfRecords(output?.clients);
  const names = clients
    .map((client) => getString(client.clientName))
    .filter((name): name is string => Boolean(name))
    .slice(0, 4);

  return {
    icon: ShieldCheck,
    title: "Klijenti koji traže pažnju",
    description:
      clients.length > 0
        ? `Izdvojeno je ${clients.length} ${pluralize(clients.length, "prioritet", "prioriteta", "prioriteta")}.`
        : (getString(output?.message) ?? "Trenutno nema hitnih prioriteta."),
    details: names,
  };
}

function renderClientConversationSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const clientName = getString(output?.clientName);
  const summary = getString(output?.summary);

  return {
    icon: MessagesSquare,
    title: "Sažetak razgovora",
    description: clientName
      ? `Pregledan je razgovor sa klijentom ${clientName}.`
      : "Pregledan je razgovor sa klijentom.",
    details: summary ? [summary] : [],
  };
}

function renderTherapistEarningsSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const totals = isRecord(output?.totals) ? output.totals : null;
  const grossAmount = getNumber(totals?.grossAmountEur) ?? 0;
  const payoutAmount = getNumber(totals?.payoutAmountEur) ?? 0;
  const unpaidAmount = getNumber(totals?.unpaidAmountEur) ?? 0;

  return {
    icon: Wallet,
    title: "Pregled zarade",
    description: `Bruto: ${grossAmount}€. Isplaćeno: ${payoutAmount}€. Neisplaćeno: ${unpaidAmount}€.`,
  };
}

function renderSessionPoliciesSummary(
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const policySummary = isRecord(output?.policySummary)
    ? output.policySummary
    : null;
  const cancellation = getString(policySummary?.cancellation);
  const reschedule = getString(policySummary?.reschedule);

  return {
    icon: ShieldCheck,
    title: "Pravila za sesiju",
    description:
      cancellation ??
      getString(output?.error) ??
      "Učitana su pravila za ovu sesiju.",
    details: reschedule ? [reschedule] : [],
  };
}

function renderFallbackToolSummary(
  toolName: string,
  output: Record<string, unknown> | null,
): AssistantTaskPresentation {
  const message =
    getString(output?.message) ??
    getString(output?.error) ??
    "Korak je završen bez dodatnih detalja.";

  return {
    icon: Sparkles,
    title: getToolLabel(toolName),
    description: message,
  };
}

function buildKnowledgeBaseSources(
  results: Record<string, unknown>[],
): AssistantTaskSourceLink[] {
  const deduped = new Map<string, AssistantTaskSourceLink>();

  for (const result of results) {
    const source = getString(result.source);
    const title = getString(result.title);
    const heading = getString(result.heading);
    const audience = getHelpAudience(result.audience);
    const href = source ? buildHelpArticleHref(source, audience) : null;

    if (!href || !title) continue;

    const key = `${href}:${heading ?? ""}`;
    if (!deduped.has(key)) {
      deduped.set(key, {
        href,
        title,
        subtitle: heading ?? undefined,
      });
    }
  }

  return Array.from(deduped.values()).slice(0, 4);
}

function buildHelpArticleHref(source: string, audience: HelpAudience | null) {
  if (!audience) return null;
  return `/podrska/${HELP_AUDIENCE_SEGMENTS[audience]}/${source}`;
}

function getHelpAudience(value: unknown): HelpAudience | null {
  return value === "client" || value === "therapist" ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getArrayOfRecords(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord);
}

function getArrayOfStrings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

function getString(value: unknown) {
  return typeof value === "string" ? value : null;
}

function getBoolean(value: unknown) {
  return typeof value === "boolean" ? value : null;
}

function getNumber(value: unknown) {
  return typeof value === "number" ? value : null;
}

function pluralize(count: number, one: string, few: string, many: string) {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  if (lastDigit === 1 && lastTwoDigits !== 11) return one;
  if (
    lastDigit >= 2 &&
    lastDigit <= 4 &&
    (lastTwoDigits < 12 || lastTwoDigits > 14)
  ) {
    return few;
  }

  return many;
}
